import React from 'react';
import { FiCheck } from 'react-icons/fi';
import { TbPin } from 'react-icons/tb';
import { cn } from '@/lib/utils';

// Trilha personalizada: `chapters` do detalhe ([{ id, chapter, status, percent-walked }]), em
// ordem decrescente. Concluído = check; em andamento = alfinete. O capítulo aberto na tela
// (`currentId`) fica em verde; os demais em cinza. Só informativo: os capítulos não são clicáveis.
// Altura máxima com scroll vertical quando a lista cresce.
export function ChapterProgressCard({ chapters = [], currentId }) {
  const ordered = [...chapters].sort((a, b) => b.chapter - a.chapter);

  return (
    <section className="rounded-[20px] border border-neutral-100 bg-white p-4 shadow-card">
      <h2 className="text-sm font-semibold text-[#222731]">Meu progresso na trilha</h2>
      <ul className="mt-3 flex max-h-[150px] flex-col gap-2.5 overflow-y-auto pr-1">
        {ordered.map(chapter => {
          const current = String(chapter.id) === String(currentId);
          const completed = chapter.status === 'completed';
          const Icon = completed ? FiCheck : TbPin;
          return (
            <li
              key={chapter.id}
              aria-current={current ? 'step' : undefined}
              className={cn('flex min-w-0 items-center gap-2 pl-3 text-sm', current ? 'font-semibold text-success' : 'text-neutral-400')}
            >
              <Icon
                aria-hidden="true"
                className="h-5 w-5 shrink-0"
              />
              <span className="truncate">Capítulo {chapter.chapter}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// Trilha fixa: seções concluídas (check verde), atual (laranja) e futuras (cinza).
// `current`/`total` = `stage-current`/`stage-total` do detalhe; `currentLabel` = nome da área atual.
// Seções com largura fixa e conectores flex-1 entre elas: a primeira encosta na
// esquerda e a última na direita, sem sobra nas pontas.
export function SectionStepper({ total, current, currentLabel, className }) {
  const sections = Array.from({ length: total }, (_, i) => i + 1);

  return (
    <nav
      aria-label="Seções da trilha"
      className={cn('rounded-[20px] border border-neutral-100 bg-white px-6 py-3 shadow-card', className)}
    >
      <ol className="flex items-start">
        {sections.map(number => {
          const done = number < current;
          const isCurrent = number === current;
          return (
            <React.Fragment key={number}>
              <li
                aria-current={isCurrent ? 'step' : undefined}
                title={isCurrent ? currentLabel : undefined}
                className="relative z-10 flex w-14 shrink-0 flex-col items-center gap-1.5"
              >
                <span className={cn('flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold', done && 'bg-success text-white', isCurrent && 'bg-mission text-white', !done && !isCurrent && 'bg-neutral-200 text-neutral-500')}>
                  {done ? <FiCheck className="h-3.5 w-3.5" /> : number}
                </span>
                <span className={cn('whitespace-nowrap text-[11px] sm:text-xs', isCurrent ? 'font-bold text-[#222731]' : 'text-neutral-400')}>Seção {number}</span>
              </li>
              {/* Conector até a próxima seção (encosta nos círculos): verde se esta seção já foi concluída */}
              {number < total && (
                <li
                  aria-hidden="true"
                  className={cn('-mx-4 mt-[11px] h-0.5 flex-1', done ? 'bg-success' : 'bg-neutral-300')}
                />
              )}
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
